import { useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@iq/react-components';
import config from '../../../../config';

import './ExportEventModal.scss';
import JsonEditor from '../../../JsonEditor';
import SimpleModal from '../../../SimpleModal';
import ExportProgress from '../../../ExportProgress';
import { getUiSchema, getEventSeriesExportSchema } from './exportEventSchema';
import {
  getIsExporting,
  fetchExportEvents,
  stopFetchExportEvents,
  getEventOrder,
} from '../../../../bundles/events';
import { getLimitedUsers } from '../../../../bundles/ad';
import { getTagsByCategory } from '../../../../bundles/tags';
import { getComponentHashmap } from '../../../../bundles/components';
import { utcToSite, datetimeString } from '../../../../datetimeUtils';
import { displayNotification, getNotification } from '../../../../bundles/notifications';
import {
  useDateSubscription,
  getPollingDateRange,
  setPollingDateRange,
  getTimezone,
} from '../../../../bundles/application';
import {
  useDtmpAlgorithmTypes,
  useFilterParameters,
  usePointGroups,
  useScopedComponentIds,
  useScopedIotComponentIds,
  useScopedToAllComps,
  useSelectedEventType,
} from '../EventTimelineState';
import { getEventBuckets } from '../../../../services';
import {
  EXPORT_EVENT_PANEL,
  EXPORT_EVENT_LIMIT,
  EVENT_TYPE_MACH,
  EVENT_TYPE_DTMP,
  EVENT_TYPE_USER,
} from '../../../../constants';
import { exportEventsToFile } from '../../../../utils';
import PieChartExport from '../ExportEventStats/ExportEventStats';

const ExportEventModal = ({
  onClose,
  panelId,
  site,
  availableIotEventTypes,
  availableUserEventTypes,
  allUserEventTypes,
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({});
  const [validating, setValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [totalEvents, setTotalEvents] = useState(0);
  const [eventRequests, setEventRequests] = useState(0);
  const [eventsData, setEventsData] = useState({});
  const eventPanelDateRange = useSelector((state) => getPollingDateRange(state, panelId));
  const inputDateRange = useDateSubscription(EXPORT_EVENT_PANEL);
  const exporting = useSelector(getIsExporting);
  const eventExportOrder = useSelector(getEventOrder);
  const allUsers = useSelector(getLimitedUsers);
  const allTags = useSelector((state) => getTagsByCategory(state, 'events'));
  const componentMaps = useSelector(getComponentHashmap);
  const scopedComponentIds = useScopedComponentIds();
  const scopedIotComponentIds = useScopedIotComponentIds();
  const scopedToAllComps = useScopedToAllComps();
  const [filterParameters] = useFilterParameters();
  const dtmpAlgorithmTypes = useDtmpAlgorithmTypes();
  const pointGroups = usePointGroups();
  const [eventsStats, setEventsStats] = useState([]);
  const [eventsStatsReturnValueCount, setEventsStatsReturnValueCount] = useState(0);
  const [selectedEventType] = useSelectedEventType();
  const hasScopedFilter = !scopedToAllComps;
  const shouldFetchScoped = !hasScopedFilter || scopedComponentIds.length > 0;

  const scopedDtmpDesignations = useMemo(() => {
    if (scopedComponentIds.length === 0 || scopedToAllComps) {
      return null;
    }
    const designations = [
      ...new Set(
        scopedComponentIds
          .map((componentId) => componentMaps[componentId]?.itemDesignation)
          .filter(Boolean)
      ),
    ];
    return designations.length > 0 ? designations : null;
  }, [componentMaps, scopedComponentIds, scopedToAllComps]);

  useEffect(() => {
    dispatch(setPollingDateRange({ ...eventPanelDateRange }, EXPORT_EVENT_PANEL));
  }, [eventPanelDateRange, dispatch]);

  useEffect(() => {
    const isIotEvent =
      selectedEventType === EVENT_TYPE_MACH || selectedEventType === EVENT_TYPE_DTMP;
    setFormData((fd) => ({
      ...fd,
      range: {
        from: inputDateRange.startDate,
        to: inputDateRange.endDate,
      },
      eventType: selectedEventType,
      ...(isIotEvent && { exportContent: 'events' }),
    }));
  }, [inputDateRange, selectedEventType]);

  const schema = useMemo(
    () =>
      getEventSeriesExportSchema(
        panelId,
        allUsers,
        allTags,
        selectedEventType,
        availableIotEventTypes,
        availableUserEventTypes,
        { filterParameters, pickerId: EXPORT_EVENT_PANEL, dtmpAlgorithmTypes, pointGroups }
      ),
    [
      panelId,
      allUsers,
      allTags,
      availableIotEventTypes,
      selectedEventType,
      availableUserEventTypes,
      filterParameters,
      dtmpAlgorithmTypes,
      pointGroups,
    ]
  );

  const tooManyEventsMessage = `Event export limit of ${EXPORT_EVENT_LIMIT} events exceeded. Please adjust filters to reduce the number of
        events.`;

  const noEventsMessage = 'There are no events to export.';

  const getTagByName = useCallback(
    (tagId) => allTags.find((tag) => tag.id === tagId)?.name || 'N/A',
    [allTags]
  );

  const getMappedUserEvents = useCallback(
    (events) =>
      events.map(({ assignedTo, createdBy, ...event }) => ({
        ...event,
        components: event.components.map((c) => componentMaps[c]?.itemDesignation || 'N/A'),
        tags: event.tags.map((t) => getTagByName(t)),
        ...(!config.onPremise && {
          createdBy: allUsers.find((user) => user.id === createdBy)?.name || 'N/A',
          assignedTo: allUsers.find((user) => user.id === assignedTo)?.name || 'N/A',
        }),
        type:
          allUserEventTypes.find((userEvent) => userEvent.id === event.type)?.name || event.type,
      })),
    [allUsers, allUserEventTypes, componentMaps, getTagByName]
  );

  const onExportDone = useCallback(() => {
    dispatch(displayNotification(getNotification('exportEvents', 'success')('Events')));
    onClose();
  }, [dispatch, onClose]);

  const handleProgressCompleted = useCallback(
    (userEvents) => {
      const { from, to } = formData.range || {};

      exportEventsToFile({
        siteName: site.name,
        events:
          formData.eventType === EVENT_TYPE_USER
            ? getMappedUserEvents(userEvents)
            : eventsData.events,
        startDate: from,
        endDate: to,
        exportType: formData.exportType,
        eventType: formData.eventType,
        onExportCompleted: onExportDone,
      });
    },
    [formData, site.name, eventsData.events, onExportDone, getMappedUserEvents]
  );

  const fetchEventsCallback = useCallback(
    (eventType, events) => {
      const eventsLength = events.length;
      if (eventsLength === 0) {
        setValidationError(noEventsMessage);
      } else if (!events) {
        onClose();
      } else if (eventType === EVENT_TYPE_USER) {
        handleProgressCompleted(events);
      } else {
        setEventsData({ events, eventsLength });
      }
    },
    [noEventsMessage, onClose, handleProgressCompleted]
  );

  const timezone = useSelector(getTimezone);
  const format = (timestamp, withTz = false) =>
    timestamp ? datetimeString(utcToSite(timestamp, timezone), timezone, false, withTz) : '-';

  const buildMachQuery = useCallback(
    (formDataProperty, query) => {
      query.from = formDataProperty.range?.from;
      query.to = formDataProperty.range?.to;
      query.interval = Math.round((query.to - query.from) / 25);
      query.types =
        formDataProperty.eventTypes?.length > 0
          ? formDataProperty.eventTypes
          : (availableIotEventTypes?.mach || []).map((type) => type.id);
      query.active = formDataProperty.active;
      if (hasScopedFilter) {
        query.components = scopedIotComponentIds;
      }
      if (formDataProperty.remoteSource?.length > 0) {
        query.pointGroups = formDataProperty.remoteSource;
      }
    },
    [availableIotEventTypes, scopedIotComponentIds, hasScopedFilter]
  );

  const buildDtmpQuery = useCallback(
    (formDataProperty, query) => {
      query.from = formDataProperty.range?.from;
      query.to = formDataProperty.range?.to;
      query.interval = Math.round((query.to - query.from) / 25);
      query.types =
        formDataProperty.eventTypes?.length > 0
          ? formDataProperty.eventTypes
          : (availableIotEventTypes?.dtmp?.eventTypes || []).map((type) => type.id);
      if (formDataProperty.algorithmType?.length > 0) {
        query.algorithmTypes = formDataProperty.algorithmType;
      }
      if (scopedDtmpDesignations) {
        query.deviatingReferenceDesignations = scopedDtmpDesignations;
      }
    },
    [availableIotEventTypes, scopedDtmpDesignations]
  );

  const buildUserQuery = useCallback(
    (formDataProperty, query) => {
      query.from = formDataProperty.range?.from;
      query.to = formDataProperty.range?.to;
      query.type =
        formDataProperty.userTypes?.length > 0
          ? formDataProperty.userTypes
          : availableUserEventTypes.map((type) => type.id);
      query.components = hasScopedFilter ? scopedComponentIds : undefined;
      const hasStatusFilter = formDataProperty.status && formDataProperty.status !== 'all';
      const hasActivityFilter = formDataProperty.activity && formDataProperty.activity !== 'all';
      if (hasStatusFilter) {
        query.state = [formDataProperty.status];
      }
      if (hasActivityFilter) {
        query.planned = formDataProperty.activity === 'planned';
      }
      // Activity/Status only apply to tasks, so general events must be excluded once either is set.
      if (hasStatusFilter || hasActivityFilter) {
        query.task = true;
      }
    },
    [availableUserEventTypes, scopedComponentIds, hasScopedFilter]
  );

  const buildEventQuery = useCallback(
    (formDataProperty, siteId) => {
      const query = {
        ...formDataProperty,
        site: siteId,
        order: eventExportOrder,
      };

      if (config.onPremise && formDataProperty.eventType === EVENT_TYPE_USER) {
        delete query.assignedTo;
        delete query.users;
      }

      if (formDataProperty.eventType === EVENT_TYPE_MACH) {
        buildMachQuery(formDataProperty, query);
      }

      if (formDataProperty.eventType === EVENT_TYPE_DTMP) {
        buildDtmpQuery(formDataProperty, query);
      }

      if (formDataProperty.eventType === EVENT_TYPE_USER) {
        buildUserQuery(formDataProperty, query);
      }

      return query;
    },
    [eventExportOrder, buildMachQuery, buildDtmpQuery, buildUserQuery]
  );

  const buildTaskQuery = useCallback(
    (formDataProperty, siteId) => ({
      site: siteId,
      order: eventExportOrder,
      statistics: true,
      withRecurring: true,
      from: formDataProperty.range?.from,
      to: formDataProperty.range?.to,
      type:
        formDataProperty.taskTypes?.length > 0
          ? formDataProperty.taskTypes
          : availableUserEventTypes.filter((x) => x.task).map((type) => type.id),
    }),
    [eventExportOrder, availableUserEventTypes]
  );

  const handleIotExport = useCallback(
    async (query) => {
      const { values } = await getEventBuckets(query);
      if (values.length === 0) return setValidationError(noEventsMessage);
      const [{ numEvents }] = values;
      if (numEvents > EXPORT_EVENT_LIMIT) {
        return setValidationError(tooManyEventsMessage);
      }
      if (numEvents === 0) {
        return setValidationError(noEventsMessage);
      }

      setTotalEvents(numEvents);
      setShowProgressModal(true);
      dispatch(
        fetchExportEvents({
          eventType: formData.eventType,
          query,
          perRequestCallback: (totalRequests) => setEventRequests(totalRequests),
          exportCallback: (events) => fetchEventsCallback(formData.eventType, events),
        })
      );
      return true;
    },
    [dispatch, formData.eventType, fetchEventsCallback, tooManyEventsMessage]
  );

  const handleUserExport = useCallback(
    (query) => {
      dispatch(
        fetchExportEvents({
          eventType: EVENT_TYPE_USER,
          query,
          exportCallback: (events) => fetchEventsCallback(EVENT_TYPE_USER, events),
        })
      );
    },
    [dispatch, fetchEventsCallback]
  );

  const handleTaskExport = useCallback(
    (query) => {
      dispatch(
        fetchExportEvents({
          eventType: EVENT_TYPE_USER,
          query,
          exportCallback: (events) => {
            if (!events || events.length === 0) {
              setValidationError(noEventsMessage);
            } else {
              setShowProgressModal(true);
              setEventsStats(events);
            }
          },
        })
      );
    },
    [dispatch]
  );

  const onFormSubmit = useCallback(async () => {
    setValidating(true);
    const isMach = formData.eventType === EVENT_TYPE_MACH;
    const isDtmp = formData.eventType === EVENT_TYPE_DTMP;
    const isUser = formData.eventType === EVENT_TYPE_USER;
    const isEventExport = formData.exportContent === 'events';

    try {
      if (!shouldFetchScoped) {
        setValidationError(noEventsMessage);
        return;
      }

      if (isEventExport) {
        const query = buildEventQuery(formData, site.id);

        if (isMach || isDtmp) {
          await handleIotExport(query);
        } else if (isUser) {
          handleUserExport(query);
        }
      } else {
        const query = buildTaskQuery(formData, site.id);
        handleTaskExport(query);
      }
    } catch (error) {
      console.error('Error exporting events:', error);
      dispatch(displayNotification(getNotification('exportEvents', 'error')()));
      onClose();
    } finally {
      setValidating(false);
    }
  }, [
    formData,
    buildEventQuery,
    buildTaskQuery,
    dispatch,
    handleIotExport,
    handleTaskExport,
    handleUserExport,
    noEventsMessage,
    onClose,
    site.id,
    shouldFetchScoped,
  ]);

  const onHandleClose = useCallback(() => onClose(), [onClose]);

  const onHandleChange = useCallback(({ formData: updatedFormData }) => {
    setValidationError('');
    setFormData((fd) => ({ ...fd, ...updatedFormData }));
  }, []);

  const selectedRange = formData.range || {};

  const onCancelExport = () => {
    dispatch(stopFetchExportEvents());
    setShowProgressModal(false);
  };

  return (
    <>
      {showProgressModal && (
        <SimpleModal
          onClose={onClose}
          title="Export data"
          size="s"
        >
          <div className="export-progress-modal">
            <ExportProgress
              exportType={formData.exportContent === 'events' ? 'event' : 'signal'}
              useStaticTimer={300}
              retrievedDataPoints={
                formData.exportContent === 'events'
                  ? eventsData.eventsLength
                  : eventsStatsReturnValueCount
              }
              increment={formData.exportContent === 'events' ? eventRequests : 0}
              expectedDataPoints={formData.exportContent === 'events' ? totalEvents : 10_000}
              onExportCompleted={handleProgressCompleted}
            />
            <Button
              className="cancel"
              activity="secondary"
              onClick={onCancelExport}
            >
              Cancel
            </Button>
          </div>
        </SimpleModal>
      )}
      {!showProgressModal && (
        <SimpleModal
          onClose={onClose}
          overlayCanClose={true}
          title={'Export events'}
          size={'m'}
        >
          <div className="export-events-modal">
            <JsonEditor
              formData={formData}
              schema={schema}
              uiSchema={getUiSchema(selectedEventType, availableUserEventTypes)}
              editorOnly
              cancelCallback={onHandleClose}
              onFormChange={onHandleChange}
              onFormSubmit={onFormSubmit}
              saveButtonText={'Export'}
              initialEditMode={true}
              showEditButton={false}
              submitDisabled={validating || exporting}
              customErrorMessage={validationError}
            />
          </div>
        </SimpleModal>
      )}

      {eventsStats?.length > 0 && (
        <PieChartExport
          site={site.name}
          eventsStats={eventsStats}
          onClose={() => {
            dispatch(displayNotification(getNotification('exportEvents', 'success')('Statistics')));
            onClose();
          }}
          setEventsStatsRetCount={setEventsStatsReturnValueCount}
          dateDisplay={`${format(selectedRange.from)} - ${format(selectedRange.to)}`}
          startDate={selectedRange.from}
          endDate={selectedRange.to}
          allUserEventTypes={allUserEventTypes}
        ></PieChartExport>
      )}
    </>
  );
};

ExportEventModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  panelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  site: PropTypes.object.isRequired,
  availableIotEventTypes: PropTypes.object.isRequired,
  availableUserEventTypes: PropTypes.array.isRequired,
  allUserEventTypes: PropTypes.array.isRequired,
};

export default ExportEventModal;
